/**
 * Meta Pixel helper — thin wrapper around window.fbq
 * Ensures safe calls even when Pixel hasn't loaded yet (deferred, consent-gated).
 * Every event goes to the browser Pixel and to CAPI with the SAME event_id,
 * so Meta deduplicates. CAPI itself re-checks marketing consent.
 */

import { sendCapiEvent } from './metaCapi';

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue?: unknown[];
      push?: unknown;
      loaded?: boolean;
      version?: string;
    };
    _fbq?: unknown;
  }
}

/** Generate a unique event_id for dedup between browser + CAPI */
export function generateEventId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function safeFbq(...args: unknown[]) {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq(...args);
  }
}

/** Once-only guard per key, persisted so reloads of a success page never double count. */
function claimOnce(key: string): boolean {
  if (typeof window === 'undefined') return false;
  const storageKey = `meta_evt_${key}`;
  try {
    if (window.localStorage.getItem(storageKey)) return false;
    window.localStorage.setItem(storageKey, String(Date.now()));
    return true;
  } catch {
    return true;
  }
}

let skippedInitialPageView = false;

/**
 * Fire a PageView on SPA route changes.
 * The Pixel bootstrap already tracks the first PageView on init, so the first
 * call (initial route) is skipped to avoid counting the landing page twice.
 */
export function trackPageView() {
  if (!skippedInitialPageView) {
    skippedInitialPageView = true;
    return;
  }
  const event_id = generateEventId();
  safeFbq('track', 'PageView', {}, { eventID: event_id });
  sendCapiEvent({ event_name: 'PageView', event_id });
}

/** Test-only: reset the initial PageView guard. */
export function __resetPageViewGuardForTests() {
  skippedInitialPageView = false;
}

/** ViewContent on the pricing page */
export function trackViewContent(contentName = 'Pricing') {
  const event_id = generateEventId();
  safeFbq('track', 'ViewContent', { content_name: contentName }, { eventID: event_id });
  sendCapiEvent({ event_name: 'ViewContent', event_id, custom_data: { content_name: contentName } });
}

/**
 * InitiateCheckout when the user heads to Stripe.
 * Value is only sent when the chosen package price is actually known.
 */
export function trackInitiateCheckout(opts?: { value?: number; currency?: string; contentName?: string }) {
  const event_id = generateEventId();
  const params: Record<string, unknown> = { content_name: opts?.contentName ?? 'Checkout' };
  if (typeof opts?.value === 'number' && opts.value > 0) {
    params.value = opts.value;
    params.currency = (opts.currency ?? 'EUR').toUpperCase();
  }
  safeFbq('track', 'InitiateCheckout', params, { eventID: event_id });
  sendCapiEvent({ event_name: 'InitiateCheckout', event_id, custom_data: params });
}

export interface ConfirmedCheckout {
  sessionId: string;
  paid: boolean;
  amountTotal: number | null; // Stripe minor units
  currency: string | null;
  contentName: string;
}

/** Build Purchase params from a Stripe-confirmed session; null when it must not be reported. */
export function buildPurchaseParams(c: ConfirmedCheckout): { value: number; currency: string; content_name: string } | null {
  if (!c.sessionId || !c.paid) return null;
  if (typeof c.amountTotal !== 'number' || c.amountTotal <= 0 || !c.currency) return null;
  return {
    value: Math.round(c.amountTotal) / 100,
    currency: c.currency.toUpperCase(),
    content_name: c.contentName,
  };
}

/**
 * Purchase — only after Stripe confirms payment, with the real amount.
 * One event per checkout session (event_id derived from the session id),
 * so reloads and browser+CAPI are deduplicated.
 */
export function trackConfirmedPurchase(c: ConfirmedCheckout): boolean {
  const params = buildPurchaseParams(c);
  if (!params) return false;
  const event_id = `purchase_${c.sessionId}`;
  if (!claimOnce(event_id)) return false;
  safeFbq('track', 'Purchase', params, { eventID: event_id });
  sendCapiEvent({ event_name: 'Purchase', event_id, custom_data: params });
  return true;
}

/** Small local-only hash so raw emails are never stored or sent as identifiers. */
function localHash(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/**
 * Lead — only after a dealer form was saved successfully. No monetary value.
 * dedupeKey (e.g. form + email) is hashed and kept only in this browser so
 * resubmits count once; the event_id sent to Meta is random.
 */
export function trackLead(contentName: string, dedupeKey?: string): boolean {
  if (dedupeKey && !claimOnce(`lead_${localHash(dedupeKey.trim().toLowerCase())}`)) return false;
  const event_id = `lead_${generateEventId()}`;
  const params = { content_name: contentName };
  safeFbq('track', 'Lead', params, { eventID: event_id });
  sendCapiEvent({ event_name: 'Lead', event_id, custom_data: params });
  return true;
}
